import React, { useEffect, useRef, useState } from 'react';
import { Button, Heading, HStack, Text, useId } from '@chakra-ui/react';
import AdminHelpTooltip from '../AdminHelpTooltip';
import { LockIcon } from '../AdminIcon';
import useAdminToast from '../useAdminToast';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import './OptionList.css';

function OptionList({ elements, name }) {
  const formId = useId();
  const toast = useAdminToast();
  const [switchStates, setSwitchStates] = useState(elements.map(element => Boolean(element.default)));
  const [inputValues, setInputValues] = useState(elements.map(element => element.default ?? ''));
  const [feedback, setFeedback] = useState({});
  const [pending, setPending] = useState({});
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const save = async (index, value, isSwitch = false) => {
    if (elements[index].disabled || pending[index]) return;
    const previous = switchStates[index];
    if (isSwitch) setSwitchStates(states => states.map((state, i) => i === index ? value : state));
    setPending(state => ({ ...state, [index]: true }));
    setFeedback(state => ({ ...state, [index]: {} }));
    try {
      const message = await elements[index].onClick(value);
      if (!mounted.current) return;
      toast({ title: message || 'Setting saved' });
    } catch (error) {
      if (!mounted.current) return;
      if (isSwitch) setSwitchStates(states => states.map((state, i) => i === index ? previous : state));
      setFeedback(state => ({ ...state, [index]: {
        error: true, message: error.message || 'Could not save this setting. Try again.',
      } }));
    } finally {
      if (mounted.current) setPending(state => ({ ...state, [index]: false }));
    }
  };

  return <div className="OptionList">
    {name && <h1 className="header">{name}</h1>}
    {elements.map((element, index) => element.visible && <React.Fragment key={`${element.type}-${index}`}>
      {element.group && <Heading as="h2" size="md" className="setting-group">{element.group}</Heading>}
      <div className="element">
      {element.type === 'switch' ? <HStack spacing={1}>
      <FormControlLabel
        control={<Switch checked={element.value ?? switchStates[index]} disabled={Boolean(element.disabled || pending[index])}
          inputProps={{ 'aria-describedby': element.description ? `setting-help-${formId}-${index}` : undefined }}
          onChange={event => save(index, event.target.checked, true)} name={element.name} color="primary" />}
        label={element.name}
      />
      {element.lockedReason && <AdminHelpTooltip label={`Why ${element.name} is locked`}
        description={element.lockedReason} icon={<LockIcon boxSize={4} />} />}
      </HStack> : element.type === 'input' ? <form className="input" onSubmit={event => {
        event.preventDefault();
        if (!pending[index] && String(inputValues[index]).trim()) save(index, inputValues[index]);
      }}>
        <TextField id={`setting-${formId}-${index}`} label={element.name} variant="outlined" value={inputValues[index]}
          onChange={event => setInputValues(values => values.map((value, i) => i === index ? event.target.value : value))}
          disabled={pending[index]} color="primary" required />
        <Button type="submit" colorScheme="blue" isLoading={Boolean(pending[index])}
          aria-label={pending[index] ? 'Saving…' : 'Save'}
          isDisabled={!String(inputValues[index]).trim()}>
          Save
        </Button>
      </form> : <Button colorScheme="red" onClick={element.onClick}>
        {element.name}
      </Button>}
      {element.description && <Text id={`setting-help-${formId}-${index}`} fontSize="sm" color="gray.600" mt={1}>{element.description}</Text>}
      {feedback[index]?.message && <div className={`confirmation${feedback[index].error ? ' admin-setting-error' : ''}`}
        role={feedback[index].error ? 'alert' : 'status'}>{feedback[index].message}</div>}
    </div></React.Fragment>)}
  </div>;
}
export default OptionList;
